import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CertificateVerificationPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialCertificateId = params.get('certificate_id') || '';
  const [certificateId, setCertificateId] = useState(initialCertificateId);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const verify = async (idToVerify) => {
    const normalized = (idToVerify || '').trim();
    if (!normalized) {
      setError('Enter a certificate ID to verify');
      setResult(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/certificates/verify`, {
        params: { certificate_id: normalized },
      });
      setResult(res.data || null);
      const nextUrl = `${window.location.pathname}?certificate_id=${encodeURIComponent(normalized)}`;
      window.history.replaceState({}, '', nextUrl);
    } catch (e) {
      setResult(null);
      setError(e.response?.data?.detail || 'Unable to verify certificate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialCertificateId) {
      verify(initialCertificateId);
    }
  }, [initialCertificateId]);

  const statusStyle = (result?.status || '').toLowerCase();
  const statusClass = statusStyle === 'valid'
    ? 'bg-green-700 text-green-100'
    : statusStyle === 'revoked'
      ? 'bg-red-700 text-red-100'
      : 'bg-gray-700 text-gray-100';

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <h1 className="text-3xl font-bold text-yellow-400">Certificate Verification</h1>
        <p className="text-gray-300">Enter a certificate ID or use a QR code link to verify certificate authenticity.</p>

        <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={certificateId}
              onChange={(e) => setCertificateId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  verify(certificateId);
                }
              }}
              className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              placeholder="e.g., KRA-ROB-2026-0012"
            />
            <button onClick={() => verify(certificateId)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-bold">
              Verify
            </button>
          </div>
          {loading && <div className="text-gray-400">Verifying...</div>}
          {error && <div className="text-red-400">{error}</div>}
        </div>

        {result && (
          <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-yellow-400">Verification Result</h2>
              <span className={`text-xs px-3 py-1 rounded-full ${statusClass}`}>{result.status || 'Unknown'}</span>
            </div>
            <div className="text-sm text-gray-300">Certificate ID: <span className="text-white">{result.certificate_id || certificateId}</span></div>
            <div className="text-sm text-gray-300">Student Name: <span className="text-white">{result.student_name || 'N/A'}</span></div>
            <div className="text-sm text-gray-300">Program: <span className="text-white">{result.program || 'N/A'}</span></div>
            <div className="text-sm text-gray-300">Course: <span className="text-white">{result.course_name || 'N/A'}</span></div>
            <div className="text-sm text-gray-300">Completion Date: <span className="text-white">{result.completion_date || 'N/A'}</span></div>
            <div className="text-sm text-gray-300">Instructor: <span className="text-white">{result.instructor || 'N/A'}</span></div>
            <div className="text-sm text-gray-300">Hours Completed: <span className="text-white">{result.hours_completed || 'N/A'}</span></div>
            {result.issue_date && <div className="text-sm text-gray-300">Issue Date: <span className="text-white">{result.issue_date}</span></div>}
            {result.revoked_reason && <div className="text-sm text-red-300">Revoked Reason: {result.revoked_reason}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
